import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { type EdgeProps, getSmoothStepPath, Position } from '@xyflow/react'

interface OrionEdgeData {
  appearKey?: string
  appearDelay?: number
  [key: string]: unknown
}

const ARROW_SIZE = 6
const ARROW_HALF_WIDTH = ARROW_SIZE * 0.7

function buildArrowTails(
  targetX: number,
  targetY: number,
  targetPosition: Position,
): { tail1: { x: number; y: number }; tail2: { x: number; y: number } } {
  switch (targetPosition) {
    case Position.Left:
      return {
        tail1: { x: targetX - ARROW_SIZE, y: targetY - ARROW_HALF_WIDTH },
        tail2: { x: targetX - ARROW_SIZE, y: targetY + ARROW_HALF_WIDTH },
      }
    case Position.Right:
      return {
        tail1: { x: targetX + ARROW_SIZE, y: targetY - ARROW_HALF_WIDTH },
        tail2: { x: targetX + ARROW_SIZE, y: targetY + ARROW_HALF_WIDTH },
      }
    case Position.Top:
      return {
        tail1: { x: targetX - ARROW_HALF_WIDTH, y: targetY - ARROW_SIZE },
        tail2: { x: targetX + ARROW_HALF_WIDTH, y: targetY - ARROW_SIZE },
      }
    case Position.Bottom:
    default:
      return {
        tail1: { x: targetX - ARROW_HALF_WIDTH, y: targetY + ARROW_SIZE },
        tail2: { x: targetX + ARROW_HALF_WIDTH, y: targetY + ARROW_SIZE },
      }
  }
}

export function OrionFlowEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style,
  data,
}: EdgeProps) {
  const [edgePath] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    borderRadius: 12,
  })

  // edge line + V 形箭头作为同一个 path (用 M sub-path 衔接)
  const fullPath = useMemo(() => {
    const { tail1, tail2 } = buildArrowTails(targetX, targetY, targetPosition)
    return `${edgePath} M ${tail1.x} ${tail1.y} L ${targetX} ${targetY} L ${tail2.x} ${tail2.y}`
  }, [edgePath, targetX, targetY, targetPosition])

  const d = (data as OrionEdgeData | undefined) ?? {}
  const appearKey = d.appearKey ?? 'default'
  const appearDelay = d.appearDelay ?? 0

  const measureRef = useRef<SVGPathElement | null>(null)
  const [length, setLength] = useState<number | null>(null)

  useLayoutEffect(() => {
    if (measureRef.current) {
      setLength(measureRef.current.getTotalLength())
    }
  }, [fullPath, appearKey])

  return (
    <>
      {/* 不可见测量 path,获取真实总长度 (包含 V 箭头部分) */}
      <path
        ref={measureRef}
        d={fullPath}
        fill="none"
        stroke="none"
        style={{ visibility: 'hidden', pointerEvents: 'none' }}
      />
      {/* 可见 edge: line + V 箭头一起绘制 (共用 strokeDashoffset 动画) */}
      <path
        id={id}
        d={fullPath}
        fill="none"
        stroke={(style?.stroke as string) ?? 'oklch(75% 0.16 65 / 0.7)'}
        strokeWidth={(style?.strokeWidth as number) ?? 1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={
          length
            ? {
                strokeDasharray: `${length}`,
                strokeDashoffset: `${length}`,
                animation: `orion-edge-draw 0.55s ${appearDelay}s cubic-bezier(0.22, 1, 0.36, 1) forwards`,
              }
            : { opacity: 0 }
        }
        className="react-flow__edge-path"
      />
    </>
  )
}
